import { useState, useMemo, useEffect, ChangeEvent, useRef, Fragment, RefObject } from 'react';
import tw from 'twin.macro';
import { CSSProp } from 'styled-components';
import { Trans } from 'react-i18next';

import { CaretIcon } from '@icons/CaretIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { ContextualTierBadge } from '@insights_module/components/ContextualTierBadge';
import { CloseIcon } from '@icons/CloseIcon';

const Container = tw.div`relative inline-block text-text-and-icon`;
const SelectOptionContainer = tw.div`flex flex-col w-[220px] h-[270px] overflow-auto bg-neutral-background border border-solid border-gray-1 rounded shadow-ui absolute top-9 left-0 overflow-x-hidden z-10 gap-0.5 text-text-and-icon`;
const SearchInput = tw.input`block w-full text-xs placeholder:text-xs placeholder:italic leading-[14px] py-[9px] px-2.5 focus:border-0 focus:outline-none bg-neutral-background`;
const Divider = tw.div`w-auto border-b border-solid border-gray-1 h-[1px]`;
export const OptionWrapper = tw.div`relative hover:bg-gray-2 rounded-sm min-h-[29px] flex justify-between items-center cursor-pointer`;
export const Option = tw.div`cursor-pointer truncate px-2 py-[6px] text-[11px] leading-4 font-medium`;
const Prefix = tw.span`-mr-1`;
const SelectLabel = tw.div`
  w-auto h-[28px] grid grid-cols-[1fr_14px] gap-1 items-center border border-solid border-gray-1 pr-2 rounded cursor-pointer shadow-ui bg-neutral-background
  [> div]:border-none
`;
const TierHeader = tw.div`h-[24px] sticky top-0 flex justify-center items-center bg-sec-4 text-[11px] font-medium text-accent-1 py-[3px] z-10 transition-all duration-200`;
const StickyTopWrapper = tw.div`sticky top-0 z-10 bg-neutral-background`;
const StickyBottomWrapper = tw.div`flex sticky bottom-0 justify-center items-center bg-neutral-background z-10 pb-[2px]`;
const CloseIconWrapper = tw.div`flex w-[20px] h-[20px] justify-center items-center`;

const MIN_SEARCH_LENGTH = 3;
const MAX_ITEMS_TO_SHOW = 7;

const CONTEXTUAL_TIER_LABELS = [
  <Trans i18nKey='insights.contextual_filters.tiers.1' key='tier-1'>
    Tier 1
  </Trans>,
  <Trans i18nKey='insights.contextual_filters.tiers.2' key='tier-2'>
    Tier 2
  </Trans>,
  <Trans i18nKey='insights.contextual_filters.tiers.3' key='tier-3'>
    Tier 3
  </Trans>,
  <Trans i18nKey='insights.contextual_filters.tiers.4' key='tier-4'>
    Tier 4
  </Trans>,
];

const emptyContextualOption: ContextualDropdownOption = {
  value: null,
  label: (
    <Option tw='text-text-and-icon-muted opacity-50'>
      {<Trans i18nKey='insights.query_builder.iab.empty_option'>Choose iAB Category</Trans>}
    </Option>
  ),
  tierLevel: 0,
  path: [],
};

const ArrowToRight = () => (
  <div tw='pr-[8px] '>
    <CaretIcon tw='fill-text-and-icon -rotate-90' />
  </div>
);
const ScrollArrowIndicator = ({
  pointTo,
  onMouseEnter,
  onMouseLeave,
}: {
  pointTo: 'top' | 'bottom';
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  return (
    <div
      tw='flex justify-center items-center py-[4px] w-full'
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <CaretIcon
        tw='fill-text-and-icon-muted-2 max-w-[10px] max-h-[10px]'
        css={[pointTo === 'top' && tw`rotate-180`]}
        style={{ transition: '' }}
      />
    </div>
  );
};

function filterOptionsByVale(
  options: ContextualDropdownOption[],
  value: string
): ContextualDropdownOption[] {
  const filteredOptions: ContextualDropdownOption[] = [];
  for (const option of options) {
    if (option.value?.toLowerCase().includes(value.toLowerCase())) {
      filteredOptions.push(option);
    }
    if (option.children) {
      const filteredChildren = filterOptionsByVale(option.children, value);
      if (filteredChildren.length) {
        filteredOptions.push(...filteredChildren);
      }
    }
  }
  return filteredOptions;
}

// we reuse this interface for language dropdown as well, if we start using it for other dropdowns we should create separate interfaces and refactor the code
export interface ContextualDropdownOption {
  value: string | null;
  label: string | JSX.Element;
  tierLevel: number;
  path: string[];
  children?: ContextualDropdownOption[];
}

interface IDropdownProps {
  options: ContextualDropdownOption[];
  value: ContextualDropdownOption | null;
  onChange: (val: ContextualDropdownOption | ContextualDropdownOption[]) => void;
  id?: string;
  searchPlaceholder?: string;
  autoFocus?: boolean;
  labelStyle?: CSSProp;
  caretStyle?: CSSProp;
  dropdownMenuStyle?: CSSProp;
  isPresentational?: boolean;
  isSearchable?: boolean;
  labelPrefix?: string;
  cancelable?: boolean;
  emptyOption?: ContextualDropdownOption;
  tierLabels?: string[] | JSX.Element[];
  onClickOutside?: (selectedOption: ContextualDropdownOption | null) => void;
}

export const IabCategoriesDropdown = ({
  id,
  value,
  options,
  searchPlaceholder = 'Search',
  onChange,
  autoFocus = false,
  labelStyle,
  caretStyle,
  dropdownMenuStyle,
  isPresentational = false,
  isSearchable = true,
  labelPrefix = '',
  cancelable = false,
  emptyOption = emptyContextualOption,
  tierLabels = CONTEXTUAL_TIER_LABELS,
  onClickOutside,
  ...rest
}: IDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(autoFocus);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useOnClickOutside(dropdownRef, () => {
    setIsFocused(false);
    onClickOutside && onClickOutside(selectedOption);
  });

  const [labelValue, setLabelValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<ContextualDropdownOption | null>(value);
  const [currentTier1Category, setCurrentTier1Category] = useState<ContextualDropdownOption | null>(
    null
  );
  const [currentTier2Category, setCurrentTier2Category] = useState<ContextualDropdownOption | null>(
    null
  );
  const [currentTier3Category, setCurrentTier3Category] = useState<ContextualDropdownOption | null>(
    null
  );

  const tier1ContentRef = useRef<HTMLDivElement>(null);
  const tier2ContentRef = useRef<HTMLDivElement>(null);
  const tier3ContentRef = useRef<HTMLDivElement>(null);
  const tier4ContentRef = useRef<HTMLDivElement>(null);

  let scrollInterval: NodeJS.Timeout;

  const showSearchResults = useMemo(() => searchTerm.length >= MIN_SEARCH_LENGTH, [searchTerm]);

  const tier2IsEnabled = useMemo(
    () => currentTier1Category && !!currentTier1Category.children?.length && !showSearchResults,
    [currentTier1Category, showSearchResults]
  );
  const tier3IsEnabled = useMemo(
    () => tier2IsEnabled && currentTier2Category && !!currentTier2Category.children?.length,
    [currentTier2Category, tier2IsEnabled]
  );
  const tier4IsEnabled = useMemo(
    () =>
      tier2IsEnabled &&
      tier3IsEnabled &&
      currentTier3Category &&
      !!currentTier3Category.children?.length,
    [currentTier3Category, tier2IsEnabled, tier3IsEnabled]
  );

  const shouldShowTier2ScrollArrows = useMemo(
    () =>
      currentTier1Category?.children?.length &&
      currentTier1Category?.children?.length > MAX_ITEMS_TO_SHOW,
    [currentTier1Category]
  );
  const shouldShowTier3ScrollArrows = useMemo(
    () =>
      currentTier2Category?.children?.length &&
      currentTier2Category?.children?.length > MAX_ITEMS_TO_SHOW,
    [currentTier2Category]
  );
  const shouldShowTier4ScrollArrows = useMemo(
    () =>
      currentTier3Category?.children?.length &&
      currentTier3Category?.children?.length > MAX_ITEMS_TO_SHOW,
    [currentTier3Category]
  );

  const handleMouseEnter = (
    contentElement: RefObject<HTMLDivElement>,
    pointTo: 'top' | 'bottom'
  ) => {
    if (!contentElement.current) return;

    scrollInterval = setInterval(() => {
      if (!contentElement.current) return;
      contentElement.current.scrollTop += pointTo === 'top' ? -1.5 : 1.5;
    }, 10);
  };

  const handleMouseLeave = () => {
    clearInterval(scrollInterval);
  };

  const _options = useMemo(() => {
    if (searchTerm.length < MIN_SEARCH_LENGTH) {
      return options;
    }

    return filterOptionsByVale(options, searchTerm);
  }, [searchTerm, options]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setSearchTerm(value);
    handleMouseLeave();
  };

  const handleOnClose = (e: MouseEvent) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange(emptyOption);
    setSearchTerm('');
  };

  useEffect(() => {
    setCurrentTier1Category(null);
    setCurrentTier2Category(null);
    setCurrentTier3Category(null);
  }, [searchTerm]);

  useEffect(() => {
    setLabelValue(value || emptyOption);
  }, [value]);

  useEffect(() => {
    if (selectedOption && onChange) {
      onChange(selectedOption);
      setSearchTerm('');
    }
  }, [selectedOption]);

  useEffect(() => {
    if (autoFocus) {
      setIsFocused(true);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!isFocused) {
      setCurrentTier1Category(null);
      setCurrentTier2Category(null);
      setCurrentTier3Category(null);
    }
  }, [isFocused]);

  // close tier 2 dropdown when tier 1 is changed
  useEffect(() => {
    if (currentTier1Category?.value) {
      setCurrentTier2Category(null);
    }
  }, [currentTier1Category]);

  // close tier 3 dropdown when tier 2 is changed
  useEffect(() => {
    if (currentTier2Category?.value) {
      setCurrentTier3Category(null);
    }
  }, [currentTier2Category]);

  return (
    <Container id={id} ref={dropdownRef}>
      <SelectLabel
        css={[
          isFocused && tw`border-accent-muted`,
          labelStyle,
          isPresentational && tw`cursor-auto [> div]:cursor-auto!`,
          labelPrefix.length && tw`grid-cols-[auto_1fr_14px]`,
        ]}
        onClick={() => !isPresentational && setIsFocused(!isFocused)}
        {...rest}>
        {labelPrefix && <Prefix>{labelPrefix}</Prefix>}
        {labelValue?.label}
        {!isPresentational && (!cancelable || (cancelable && !selectedOption?.value)) && (
          <CaretIcon tw='fill-text-and-icon-muted w-2.5 h-2.5' css={[caretStyle]} />
        )}
        {!isPresentational && cancelable && !!selectedOption?.value && (
          <CloseIconWrapper onClick={(e) => handleOnClose(e as unknown as MouseEvent)}>
            <CloseIcon tw='fill-text-and-icon-muted w-2.5 h-2.5' css={[caretStyle]} />
          </CloseIconWrapper>
        )}
      </SelectLabel>

      {isFocused && (
        <SelectOptionContainer
          ref={tier1ContentRef}
          css={[showSearchResults && tw`w-[500px] px-[4px]`, dropdownMenuStyle]}>
          <StickyTopWrapper>
            {tierLabels[0] && (
              <TierHeader css={[showSearchResults && tw`h-0 p-0 opacity-0 overflow-hidden`]}>
                {tierLabels[0]}
              </TierHeader>
            )}
            {isSearchable && (
              <>
                <SearchInput
                  autoFocus
                  data-testid='dropdown-search'
                  type='text'
                  placeholder={searchPlaceholder}
                  onChange={(e) => handleSearch(e as ChangeEvent<HTMLInputElement>)}
                />
              </>
            )}
            <Divider />
            {_options.length > MAX_ITEMS_TO_SHOW && (
              <ScrollArrowIndicator
                pointTo='top'
                onMouseEnter={() => handleMouseEnter(tier1ContentRef, 'top')}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </StickyTopWrapper>

          {searchTerm.length < MIN_SEARCH_LENGTH
            ? _options.map((option: ContextualDropdownOption, index) => (
                <Fragment key={`${option.value} + ${option.tierLevel}`}>
                  <OptionWrapper
                    css={[currentTier1Category?.value === option.value && tw`bg-gray-2`]}
                    onClick={() => {
                      setSelectedOption(option);
                      setIsFocused(false);
                    }}
                    onMouseEnter={() => setCurrentTier1Category(option)}
                    key={index}>
                    {option.label}
                    {!!option.children?.length && <ArrowToRight />}
                  </OptionWrapper>
                </Fragment>
              ))
            : _options.map((option: ContextualDropdownOption, index) => (
                <Fragment key={`${option.value} + ${option.tierLevel}`}>
                  <OptionWrapper
                    tw='min-h-[44px] items-center pl-[4px] pb-[2px]'
                    css={[currentTier1Category?.value === option.value && tw`bg-gray-2`]}
                    onClick={() => {
                      setSelectedOption(option);
                      setIsFocused(false);
                    }}
                    onMouseEnter={() => setCurrentTier1Category(option)}
                    key={index}>
                    <div tw='flex flex-col truncate items-start w-full'>
                      <div tw='flex gap-[2px] items-center'>
                        <ContextualTierBadge tier={option.tierLevel} />
                        {option.label}
                      </div>
                      <div tw='flex gap-[2px] items-center pl-[30px] truncate'>
                        {option.path.map((ancestor, index) => (
                          <div key={index} tw='text-[10px] flex gap-[2px] text-text-and-icon-muted'>
                            <div tw='max-w-[200px] truncate'>{ancestor}</div>
                            {index < option.path.length - 1 && '>'}
                          </div>
                        ))}
                      </div>
                    </div>
                  </OptionWrapper>
                </Fragment>
              ))}

          {_options.length > MAX_ITEMS_TO_SHOW && (
            <StickyBottomWrapper>
              <ScrollArrowIndicator
                pointTo='bottom'
                onMouseEnter={() => handleMouseEnter(tier1ContentRef, 'bottom')}
                onMouseLeave={handleMouseLeave}
              />
            </StickyBottomWrapper>
          )}
        </SelectOptionContainer>
      )}

      {tier2IsEnabled && (
        <SelectOptionContainer tw='left-[224px]' css={[dropdownMenuStyle]} ref={tier2ContentRef}>
          <StickyTopWrapper>
            {currentTier1Category && tierLabels[1] && <TierHeader>{tierLabels[1]}</TierHeader>}
            {shouldShowTier2ScrollArrows && (
              <ScrollArrowIndicator
                pointTo='top'
                onMouseEnter={() => handleMouseEnter(tier2ContentRef, 'top')}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </StickyTopWrapper>

          {currentTier1Category?.children?.map((option: ContextualDropdownOption, index) => (
            <Fragment key={option.value}>
              <OptionWrapper
                css={[currentTier2Category?.value === option.value && tw`bg-gray-2`]}
                onClick={() => {
                  setSelectedOption(option);
                  setIsFocused(false);
                }}
                onMouseEnter={() => setCurrentTier2Category(option)}
                key={index}>
                {option.label}
                {!!option.children?.length && <ArrowToRight />}
              </OptionWrapper>
            </Fragment>
          ))}

          {shouldShowTier2ScrollArrows && (
            <StickyBottomWrapper>
              <ScrollArrowIndicator
                pointTo='bottom'
                onMouseEnter={() => handleMouseEnter(tier2ContentRef, 'bottom')}
                onMouseLeave={handleMouseLeave}
              />
            </StickyBottomWrapper>
          )}
        </SelectOptionContainer>
      )}

      {tier3IsEnabled && (
        <SelectOptionContainer tw='left-[448px]' css={[dropdownMenuStyle]} ref={tier3ContentRef}>
          <StickyTopWrapper>
            {currentTier2Category && tierLabels[2] && <TierHeader>{tierLabels[2]}</TierHeader>}
            {shouldShowTier3ScrollArrows && (
              <ScrollArrowIndicator
                pointTo='top'
                onMouseEnter={() => handleMouseEnter(tier3ContentRef, 'top')}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </StickyTopWrapper>

          {currentTier2Category?.children?.map((option: ContextualDropdownOption, index) => (
            <Fragment key={option.value}>
              <OptionWrapper
                css={[currentTier3Category?.value === option.value && tw`bg-gray-2`]}
                onClick={() => {
                  setSelectedOption(option);
                  setIsFocused(false);
                }}
                onMouseEnter={() => setCurrentTier3Category(option)}
                key={index}>
                {option.label}
                {!!option.children?.length && <ArrowToRight />}
              </OptionWrapper>
            </Fragment>
          ))}

          {shouldShowTier3ScrollArrows && (
            <StickyBottomWrapper>
              <ScrollArrowIndicator
                pointTo='bottom'
                onMouseEnter={() => handleMouseEnter(tier3ContentRef, 'bottom')}
                onMouseLeave={handleMouseLeave}
              />
            </StickyBottomWrapper>
          )}
        </SelectOptionContainer>
      )}

      {tier4IsEnabled && (
        <SelectOptionContainer tw='left-[672px]' css={[dropdownMenuStyle]} ref={tier4ContentRef}>
          <StickyTopWrapper>
            {currentTier3Category && tierLabels[3] && <TierHeader>{tierLabels[3]}</TierHeader>}
            {shouldShowTier4ScrollArrows && (
              <ScrollArrowIndicator
                pointTo='top'
                onMouseEnter={() => handleMouseEnter(tier4ContentRef, 'top')}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </StickyTopWrapper>

          {currentTier3Category?.children?.map((option: ContextualDropdownOption, index) => (
            <Fragment key={option.value}>
              <OptionWrapper
                onClick={() => {
                  setSelectedOption(option);
                  setIsFocused(false);
                }}
                key={index}>
                {option.label}
                {!!option.children?.length && <ArrowToRight />}
              </OptionWrapper>
            </Fragment>
          ))}

          {shouldShowTier4ScrollArrows && (
            <StickyBottomWrapper>
              <ScrollArrowIndicator
                pointTo='bottom'
                onMouseEnter={() => handleMouseEnter(tier4ContentRef, 'bottom')}
                onMouseLeave={handleMouseLeave}
              />
            </StickyBottomWrapper>
          )}
        </SelectOptionContainer>
      )}
    </Container>
  );
};

export function getOptions(
  tierData: object,
  tierLevel: number,
  currentPath: string[] = []
): ContextualDropdownOption[] {
  return Object.keys(tierData).map((key) => {
    const subTier = tierData[key as keyof typeof tierData];
    const newPath = [...currentPath, key];
    return {
      value: key,
      label: <Option>{key}</Option>,
      children: getOptions(subTier, tierLevel + 1, newPath),
      tierLevel,
      path: newPath,
    };
  });
}

export function findOptionByValue(
  options: ContextualDropdownOption[],
  value: string
): ContextualDropdownOption | null {
  for (const option of options) {
    if (option.value === value) {
      return option;
    }
    if (option.children) {
      const foundOption = findOptionByValue(option.children, value);
      if (foundOption) {
        return foundOption;
      }
    }
  }
  return null;
}
